/**
 * Estado operacional único da lista ecommerce (filtro + chip).
 * Prioridade fixa, sem IO — lê só o cache em memória.
 * @module patientEcommerceOpState
 */

import { oids } from "@/configs/Settings";
import { peekPatientReleaseSummary } from "src-next/modules/patient-supporting-documents";
import type { Patient } from "fhir/r5";

import { peekPatientDocSummary } from "./patientDocStatusCache";

export type PatientEcommerceOpState =
  | "docs_pending"
  | "docs_to_approve"
  | "docs_rejected"
  | "awaiting_release"
  | "docs_released"
  | "purchase_completed"
  | "no_documents"
  | "loading"
  | "unknown";

/** Referência FHIR estável para cache de docs/liberações na lista. */
export function ecommercePatientReference(patient: Patient): string {
  if (patient.id) return `Patient/${patient.id}`;
  const cpf =
    patient.identifier?.find((identifier) =>
      identifier.type?.coding?.some((coding) => coding.code === "TAX"),
    )?.value ||
    patient.identifier?.[0]?.value ||
    "";
  return cpf ? `Patient/${oids.cpf}-${cpf}` : "";
}

/**
 * Primeiro match vence:
 * 1. docs_rejected — qualquer documento ATIVO recusado pela loja
 * 2. docs_pending — falta pelo menos um documento obrigatório
 * 3. docs_to_approve — documento enviado, aguardando verificação da loja
 * 4. docs_released — liberação válida ativa
 * 5. awaiting_release — todos verificados e sem liberação válida
 */
export function resolvePatientEcommerceOpState(
  patient: Patient,
): PatientEcommerceOpState {
  const key = ecommercePatientReference(patient);
  if (!key) return "unknown";

  const docs = peekPatientDocSummary(key);
  if (!docs || docs.status === "unknown") return "loading";

  if (docs.hasRejectedDocuments) return "docs_rejected";

  if (docs.hasNoDocuments || docs.hasMissingDocuments) {
    return "docs_pending";
  }

  if (docs.hasPendingValidation) return "docs_to_approve";

  const releases = peekPatientReleaseSummary(key);
  if (releases.hasValid) return "docs_released";
  if (docs.allVerified) return "awaiting_release";

  return "unknown";
}
